import { useState, useEffect } from 'react';
import { Wallet, HelpCircle, TrendingUp, Blocks, ArrowRightLeft, Shield, Coins, Flame, Menu, X, Home, Globe, Mail, BarChart3, AlertTriangle, Lock, Zap, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logoImage from '@/assets/tronscope-logo.png';

import { useWallet } from '@/contexts/WalletContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useStakingConfig } from '@/hooks/useStakingConfig';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';

const TronHeader = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { address, isConnected, openConnectModal, trxBalance, usdtBalance } = useWallet();
  const { user, wallet: authWallet, wallets, walletsLoading } = useAuth();
  
  const [emailWalletBalance, setEmailWalletBalance] = useState({ trx: 0, usdt: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isStakingEnabled, effectiveApy } = useStakingConfig();

  // Fetch balance for email-authenticated users
  useEffect(() => {
    const fetchEmailWalletBalance = async () => {
      if (!user || !authWallet?.wallet_address) return;
      
      try {
        const { data, error } = await supabase.functions.invoke('tron-api', {
          body: { type: 'account', query: authWallet.wallet_address }
        });
        
        if (!error && data) {
          const trxBal = data.balance ? data.balance / 1_000_000 : 0;
          let usdtBal = 0;
          
          // USDT contract address on TRON
          const USDT_CONTRACT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t';
          if (data.trc20 && Array.isArray(data.trc20)) {
            const usdtEntry = data.trc20.find((t: any) => t[USDT_CONTRACT]);
            if (usdtEntry) {
              usdtBal = parseInt(usdtEntry[USDT_CONTRACT]) / 1_000_000;
            }
          }
          
          setEmailWalletBalance({ trx: trxBal, usdt: usdtBal });
        }
      } catch (err) {
        console.error('Failed to fetch email wallet balance:', err);
      }
    };

    fetchEmailWalletBalance();
  }, [user, authWallet?.wallet_address]);

  // Calculate total balance in USD
  const TRX_PRICE = 0.276;
  const isEmailUser = !!user && !!authWallet?.wallet_address;
  const displayTrx = isEmailUser ? emailWalletBalance.trx : (trxBalance || 0);
  const displayUsdt = isEmailUser ? emailWalletBalance.usdt : (usdtBalance || 0);
  const totalBalance = (displayTrx * TRX_PRICE) + displayUsdt;
  const formatBalance = (val: number) => val >= 1000 ? `$${(val / 1000).toFixed(1)}k` : `$${val.toFixed(0)}`;

  // Check if email user needs to verify email code or complete wallet setup
  const emailMetadata = user ? ((user.user_metadata as any) || {}) : {};
  const emailVerifiedByCode = Boolean((emailMetadata as { email_verified_code?: boolean }).email_verified_code);
  const needsEmailVerification = !!user && !emailVerifiedByCode;
  const hasAnyWallets = !!user && wallets.length > 0;
  const needsWalletSetup = !!user && emailVerifiedByCode && !walletsLoading && !hasAnyWallets;
  const needsBackupVerification = !!user && !!authWallet && !authWallet.backup_verified;

  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  // Blockchain menu - core explorer features
  const blockchainLinks = [
    { label: t('nav.blocks'), path: '/blocks', icon: Blocks, description: t('nav.blocksDesc'), color: 'text-primary' },
    { label: t('nav.transactions'), path: '/transactions', icon: ArrowRightLeft, description: t('nav.transactionsDesc'), color: 'text-cyan-500' },
    { label: t('nav.validators'), path: '/validators', icon: Shield, description: t('nav.validatorsDesc'), color: 'text-amber-500' },
    { label: t('nav.nodes'), path: '/nodes', icon: Globe, description: t('nav.nodesDesc'), color: 'text-emerald-500' },
  ];

  // Tokens menu
  const tokenLinks = [
    { label: t('nav.allTokens'), path: '/tokens', icon: Coins, description: t('nav.allTokensDesc'), color: 'text-green-500' },
    { label: t('nav.transfers'), path: '/transactions', icon: BarChart3, description: t('nav.transfersDesc'), color: 'text-blue-500' },
  ];

  // Earn menu - staking and resources
  const earnLinks = [
    ...(isStakingEnabled ? [{ label: t('nav.staking'), path: '/staking', icon: Lock, description: t('nav.stakingDesc'), color: 'text-green-500', badge: `${effectiveApy.usdt}% APY` }] : []),
    { label: t('nav.resources'), path: '/resources', icon: Zap, description: t('nav.resourcesDesc'), color: 'text-yellow-500' },
  ];

  // More menu - support and info
  const moreLinks = [
    { label: t('nav.faq'), path: '/faq', icon: HelpCircle, description: t('nav.faqDesc'), color: 'text-purple-500' },
    { label: t('nav.apiDocs'), path: '/api-docs', icon: Flame, description: t('nav.apiDocsDesc'), color: 'text-orange-500' },
    { label: t('nav.contact'), path: '/contact', icon: Mail, description: t('nav.contactDesc'), color: 'text-cyan-500' },
  ];

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const allNavLinks = [
    { label: t('header.home'), path: '/', icon: Home, color: 'text-primary' },
    ...blockchainLinks,
    ...tokenLinks,
    ...earnLinks,
    ...moreLinks,
  ];

  const handleMobileNavClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const renderNavDropdown = (
    title: string, 
    links: typeof blockchainLinks, 
    colorClass: string = 'bg-primary/15'
  ) => (
    <NavigationMenuItem>
      <NavigationMenuTrigger className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground bg-transparent hover:bg-muted/50 data-[state=open]:bg-muted/50 data-[state=open]:text-foreground rounded-xl">
        {title}
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <div className="relative w-[300px] p-2 overflow-hidden">
          {/* Glass shine effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          
          <ul className="relative grid gap-1">
            {links.map((link) => (
              <li key={link.path}>
                <NavigationMenuLink asChild>
                  <button
                    onClick={() => navigate(link.path)}
                    className="flex items-center gap-3 w-full p-2.5 rounded-xl hover:bg-white/10 transition-all duration-200 text-left group border border-transparent hover:border-white/10"
                  >
                    <div className={`w-9 h-9 rounded-xl ${colorClass} border border-white/10 flex items-center justify-center transition-transform duration-200 group-hover:scale-110 shadow-lg`}>
                      <link.icon className={`w-4 h-4 ${link.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-foreground">{link.label}</p>
                        {'badge' in link && (link as { badge?: string }).badge && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-500 border border-green-500/20">
                            {(link as { badge?: string }).badge}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground truncate">{link.description}</p>
                    </div>
                  </button>
                </NavigationMenuLink>
              </li>
            ))}
          </ul>
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );

  return (
    <header className="sticky top-0 z-50 px-2 pt-2">
      {/* Glass Header Container */}
      <div className="relative rounded-2xl sm:rounded-3xl bg-gradient-to-br from-background/95 via-muted/60 to-background/95 backdrop-blur-xl border border-border/50 shadow-lg overflow-visible">
        {/* Glass shine effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        
        <div className="relative container mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 sm:gap-2 hover:opacity-80 transition-all duration-300 group"
            >
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-primary/15 border border-primary/20 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary drop-shadow-[0_0_8px_hsl(var(--primary)/0.6)]" />
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-base sm:text-lg tracking-tight">
                  <span className="text-primary">Tron</span><span className="text-foreground">scope</span>
                </span>
              </div>
            </button>

            {/* Desktop Navigation */}
            <NavigationMenu className="hidden lg:flex">
              <NavigationMenuList className="gap-1">
                {/* Home */}
                <NavigationMenuItem>
                  <NavigationMenuLink
                    href="/"
                    onClick={(e) => { e.preventDefault(); navigate('/'); }}
                    className={`px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200 cursor-pointer ${
                      isActive('/') 
                        ? 'text-primary bg-primary/15 border border-primary/20' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    {t('header.home')}
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {renderNavDropdown(t('header.blockchain'), blockchainLinks, 'bg-primary/15')}
                {renderNavDropdown(t('header.tokens'), tokenLinks, 'bg-green-500/15')}
                {renderNavDropdown(t('header.earn'), earnLinks, 'bg-emerald-500/15')}
                {renderNavDropdown(t('header.more'), moreLinks, 'bg-purple-500/15')}
              </NavigationMenuList>
            </NavigationMenu>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Mobile menu button */}
              <Button 
                variant="ghost" 
                size="icon" 
                className="lg:hidden h-9 w-9 rounded-xl bg-muted/50 border border-border/50 hover:bg-muted"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              

              {/* Email User Wallet Button */}
              {user && (
                <>
                  {/* Desktop */}
                  {needsEmailVerification ? (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate('/auth')}
                      className="hidden sm:flex gap-2 rounded-xl border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20"
                    >
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="text-sm">{t('auth.verifyEmail', 'Verify Email')}</span>
                    </Button>
                  ) : needsWalletSetup ? (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate('/wallet-setup')}
                      className="hidden sm:flex gap-2 rounded-xl border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20"
                    >
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="text-sm">{t('wallet.createWallet', 'Create Wallet')}</span>
                    </Button>
                  ) : needsBackupVerification ? (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate('/wallet-setup')}
                      className="hidden sm:flex gap-2 rounded-xl border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20"
                    >
                      <Shield className="w-4 h-4 text-amber-500" />
                      <span className="text-sm">{t('wallet.continueBackup', 'Continue Backup')}</span>
                    </Button>
                  ) : (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate('/wallet')}
                      className="hidden sm:flex gap-2 rounded-xl border-primary/30 bg-primary/10 hover:bg-primary/20 shadow-[0_0_15px_hsl(var(--primary)/0.2)] hover:shadow-[0_0_20px_hsl(var(--primary)/0.3)] transition-all duration-300"
                    >
                      <div className="relative">
                        <Wallet className="w-4 h-4 text-primary" />
                        <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      </div>
                      <span className="font-medium text-sm">{t('wallet.myWallet', 'Wallet')}</span>
                      <span className="text-xs text-muted-foreground">{formatBalance(totalBalance)}</span>
                    </Button>
                  )}
                  
                  {/* Mobile */}
                  {needsEmailVerification ? (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => navigate('/auth')}
                      className="sm:hidden h-9 w-9 rounded-xl border border-amber-500/30 bg-amber-500/10"
                    >
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                    </Button>
                  ) : needsWalletSetup || needsBackupVerification ? (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => navigate('/wallet-setup')}
                      className="sm:hidden h-9 w-9 rounded-xl border border-amber-500/30 bg-amber-500/10"
                    >
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                    </Button>
                  ) : (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => navigate('/wallet')}
                      className="sm:hidden h-9 w-9 rounded-xl relative bg-primary/15 border border-primary/30 shadow-[0_0_15px_hsl(var(--primary)/0.3)]"
                    >
                      <Wallet className="w-4 h-4 text-primary" />
                      <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border border-background animate-pulse" />
                    </Button>
                  )}
                </>
              )}

              {/* External Wallet Button */}
              {!user && isConnected && address && (
                <>
                  {/* Desktop */}
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => navigate('/wallet')}
                    className="hidden sm:flex gap-2 rounded-xl border-primary/30 bg-primary/10 hover:bg-primary/20 shadow-[0_0_15px_hsl(var(--primary)/0.2)] hover:shadow-[0_0_20px_hsl(var(--primary)/0.3)] transition-all duration-300"
                  >
                    <div className="relative">
                      <Wallet className="w-4 h-4 text-primary" />
                      <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                    </div>
                    <span className="font-medium text-sm">{t('wallet.myWallet', 'Wallet')}</span>
                    <span className="text-xs text-muted-foreground">{formatBalance(totalBalance)}</span>
                  </Button>
                  
                  {/* Mobile */}
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => navigate('/wallet')}
                    className="sm:hidden h-9 w-9 rounded-xl relative bg-primary/15 border border-primary/30 shadow-[0_0_15px_hsl(var(--primary)/0.3)]"
                  >
                    <Wallet className="w-4 h-4 text-primary" />
                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border border-background animate-pulse" />
                  </Button>
                </>
              )}
              
              {/* Connect Wallet Button (not connected) */}
              {!user && !isConnected && (
                <>
                  {/* Desktop */}
                  <Button 
                    size="sm" 
                    onClick={openConnectModal}
                    className="hidden sm:flex rounded-xl bg-gradient-to-r from-primary to-primary/80 text-primary-foreground hover:from-primary/90 hover:to-primary/70 shadow-[0_0_20px_hsl(var(--primary)/0.4)] hover:shadow-[0_0_25px_hsl(var(--primary)/0.5)] transition-all duration-300"
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    <span className="text-sm">{t('header.connect')}</span>
                  </Button>
                  
                  {/* Mobile */}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="sm:hidden h-9 w-9 rounded-xl bg-primary/15 border border-primary/30 shadow-[0_0_15px_hsl(var(--primary)/0.3)]" 
                    onClick={openConnectModal}
                  >
                    <Wallet className="w-4 h-4 text-primary" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation Menu - Glass Style */}
      {mobileMenuOpen && (
        <div className="lg:hidden mt-2 mx-0 animate-in slide-in-from-top-2 duration-200">
          <div className="relative p-2 rounded-2xl bg-gradient-to-br from-background/95 via-muted/60 to-background/95 backdrop-blur-xl border border-border/50 shadow-xl overflow-hidden">
            {/* Glass shine effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            
            <nav className="relative grid grid-cols-2 gap-1.5">
              {allNavLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <button
                    key={link.path}
                    onClick={() => handleMobileNavClick(link.path)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-200 touch-manipulation ${
                      isActive(link.path)
                        ? 'bg-primary/15 border border-primary/20 text-primary'
                        : 'text-foreground hover:bg-muted/50 border border-transparent'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                      isActive(link.path) 
                        ? 'bg-primary/20 shadow-[0_0_10px_hsl(var(--primary)/0.3)]' 
                        : 'bg-muted/50'
                    }`}>
                      <Icon className={`w-5 h-5 ${isActive(link.path) ? 'text-primary' : link.color}`} />
                    </div>
                    <span className="text-xs font-medium truncate max-w-full">{link.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default TronHeader;
